import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Slider,
  Chip,
  Paper,
  IconButton,
  Checkbox,
} from '@mui/material';
import {
  Close,
  ArrowBack,
  ArrowForward,
  CheckCircle,
  School,
  Psychology,
  Speed,
  Palette,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { setGettingStartedData, setGettingStartedCompleted } from 'src/store/slices/tutorialSlice';
import { GettingStartedData } from 'src/types/tutorial';

interface GettingStartedWizardProps {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

const GettingStartedWizard: React.FC<GettingStartedWizardProps> = ({
  open,
  onClose,
  onComplete,
}) => {
  const dispatch = useAppDispatch();
  const { gettingStartedData } = useAppSelector((state) => state.tutorial);
  
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<GettingStartedData>({
    educationalLevel: gettingStartedData?.educationalLevel || '',
    learningGoals: gettingStartedData?.learningGoals || [],
    experienceLevel: gettingStartedData?.experienceLevel || '',
    learningStyle: gettingStartedData?.learningStyle || '',
    timeCommitment: gettingStartedData?.timeCommitment || 5,
    completed: false,
  });

  const steps = [
    'Educational Level',
    'Learning Goals',
    'Experience Level',
    'Learning Style',
    'Time Commitment',
    'Review',
  ];

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleComplete();
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleComplete = () => {
    const completedData = { ...formData, completed: true };
    dispatch(setGettingStartedData(completedData));
    dispatch(setGettingStartedCompleted(true));
    onComplete?.();
    onClose();
  };

  const handleClose = () => {
    // Save progress
    dispatch(setGettingStartedData(formData));
    onClose();
  };

  const updateFormData = (field: keyof GettingStartedData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <FormControl fullWidth>
              <InputLabel>Educational Level</InputLabel>
              <Select
                value={formData.educationalLevel}
                label="Educational Level"
                onChange={(e) => updateFormData('educationalLevel', e.target.value)}
              >
                <MenuItem value="secondary">Secondary School</MenuItem>
                <MenuItem value="university">University</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              This helps us recommend appropriate learning paths and content for you.
            </Typography>
          </Box>
        );

      case 1:
        return (
          <Box>
            <FormLabel component="legend">What are your learning goals?</FormLabel>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.learningGoals.includes('robotics')}
                    onChange={(e) => {
                      const goals = e.target.checked
                        ? [...formData.learningGoals, 'robotics']
                        : formData.learningGoals.filter((g) => g !== 'robotics');
                      updateFormData('learningGoals', goals);
                    }}
                  />
                }
                label="Robotics"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.learningGoals.includes('ai')}
                    onChange={(e) => {
                      const goals = e.target.checked
                        ? [...formData.learningGoals, 'ai']
                        : formData.learningGoals.filter((g) => g !== 'ai');
                      updateFormData('learningGoals', goals);
                    }}
                  />
                }
                label="Artificial Intelligence"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.learningGoals.includes('both')}
                    onChange={(e) => {
                      const goals = e.target.checked
                        ? [...formData.learningGoals, 'both']
                        : formData.learningGoals.filter((g) => g !== 'both');
                      updateFormData('learningGoals', goals);
                    }}
                  />
                }
                label="Both"
              />
            </Box>
            {formData.learningGoals.length > 0 && (
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {formData.learningGoals.map((goal) => (
                  <Chip key={goal} label={goal} size="small" />
                ))}
              </Box>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <FormControl component="fieldset">
              <FormLabel component="legend">What's your experience level?</FormLabel>
              <RadioGroup
                value={formData.experienceLevel}
                onChange={(e) => updateFormData('experienceLevel', e.target.value)}
                sx={{ mt: 1 }}
              >
                <FormControlLabel
                  value="beginner"
                  control={<Radio />}
                  label="Beginner - New to robotics and AI"
                />
                <FormControlLabel
                  value="intermediate"
                  control={<Radio />}
                  label="Intermediate - Some experience"
                />
                <FormControlLabel
                  value="advanced"
                  control={<Radio />}
                  label="Advanced - Experienced developer"
                />
              </RadioGroup>
            </FormControl>
          </Box>
        );

      case 3:
        return (
          <Box>
            <FormControl component="fieldset">
              <FormLabel component="legend">How do you prefer to learn?</FormLabel>
              <RadioGroup
                value={formData.learningStyle}
                onChange={(e) => updateFormData('learningStyle', e.target.value)}
                sx={{ mt: 1 }}
              >
                <FormControlLabel
                  value="visual"
                  control={<Radio />}
                  label="Visual - I learn best by seeing and visualizing"
                />
                <FormControlLabel
                  value="hands-on"
                  control={<Radio />}
                  label="Hands-on - I learn by doing and experimenting"
                />
                <FormControlLabel
                  value="theoretical"
                  control={<Radio />}
                  label="Theoretical - I prefer understanding concepts first"
                />
              </RadioGroup>
            </FormControl>
          </Box>
        );

      case 4:
        return (
          <Box>
            <Typography gutterBottom>
              How many hours per week can you dedicate to learning?
            </Typography>
            <Box sx={{ px: 2, mt: 3 }}>
              <Slider
                value={formData.timeCommitment}
                onChange={(_, value) => updateFormData('timeCommitment', value)}
                min={1}
                max={20}
                step={1}
                marks
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value} hours/week`}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              {formData.timeCommitment} hours per week
            </Typography>
          </Box>
        );

      case 5:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Your Preferences
            </Typography>
            <Paper sx={{ p: 2, mt: 2, bgcolor: 'background.default' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Educational Level
                  </Typography>
                  <Typography>{formData.educationalLevel || 'Not set'}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Learning Goals
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                    {formData.learningGoals.length > 0
                      ? formData.learningGoals.map((goal) => (
                          <Chip key={goal} label={goal} size="small" />
                        ))
                      : 'Not set'}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Experience Level
                  </Typography>
                  <Typography>{formData.experienceLevel || 'Not set'}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Learning Style
                  </Typography>
                  <Typography>{formData.learningStyle || 'Not set'}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Time Commitment
                  </Typography>
                  <Typography>{formData.timeCommitment} hours per week</Typography>
                </Box>
              </Box>
            </Paper>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              We'll use this information to personalize your learning experience!
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return !!formData.educationalLevel;
      case 1:
        return formData.learningGoals.length > 0;
      case 2:
        return !!formData.experienceLevel;
      case 3:
        return !!formData.learningStyle;
      case 4:
        return formData.timeCommitment > 0;
      case 5:
        return true;
      default:
        return false;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '500px',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <School color="primary" />
          <Typography variant="h6">Getting Started</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 4, mt: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ minHeight: '300px' }}>{renderStepContent()}</Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Button
          onClick={handleBack}
          disabled={activeStep === 0}
          startIcon={<ArrowBack />}
        >
          Back
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleClose} color="inherit">
            Save & Continue Later
          </Button>
          <Button
            onClick={handleNext}
            variant="contained"
            disabled={!canProceed()}
            endIcon={activeStep === steps.length - 1 ? <CheckCircle /> : <ArrowForward />}
          >
            {activeStep === steps.length - 1 ? 'Complete' : 'Next'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default GettingStartedWizard;

